import { motion } from 'framer-motion';
import { X, ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { useMemo } from 'react';

export const PrepaidListModal = ({ isOpen, onClose, activeCustomer, prepaidJobs = [] }: any) => {
  const history = useMemo(() => {
    if (!activeCustomer) return [];
    const prepaids =
      (typeof activeCustomer.prepaid === "string"
        ? JSON.parse(activeCustomer.prepaid)
        : activeCustomer.prepaid) || [];

    let history: any[] = [];
    
    let totalBoughtStr = 0;
    let totalBoughtAmt = 0;
    let totalUsedStr = 0;
    let totalUsedAmt = 0;

    // 1. Add Entradas
    prepaids.forEach((p: any) => {
      const pStr = parseInt(p.numStringings || '0');
      const pAmt = parseFloat((p.amount?.toString() || '0').replace(',', '.'));
      totalBoughtStr += pStr;
      totalBoughtAmt += pAmt;
      totalUsedStr += p.usedStringings || 0;
      totalUsedAmt += p.usedAmount || 0;

      if (pStr > 0 || pAmt > 0) {
        history.push({
          date: new Date(p.date),
          type: 'entrada',
          description: 'Compra de pacote pré-pago',
          qtyStringings: pStr,
          qtyAmount: pAmt
        });
      }
    });

    let jobsUsedStr = 0;
    let jobsUsedAmt = 0;

    // 2. Add Baixas (Usos)
    prepaidJobs.forEach((job: any) => {
      let qtyStr = 0;
      let qtyAmt = 0;
      
      if (job.isStringing !== false) {
        qtyStr = -1;
      } else if (job.price) {
        qtyAmt = -parseFloat(job.price);
      } else {
        qtyStr = -1;
      }

      jobsUsedStr += Math.abs(qtyStr);
      jobsUsedAmt += Math.abs(qtyAmt);

      let jobDate = new Date();
      if (job.createdAt) {
        jobDate = new Date(job.createdAt);
      } else if (job.date) {
        const parts = job.date.split('/');
        if (parts.length === 3) jobDate = new Date(parseInt(parts[2]), parseInt(parts[1])-1, parseInt(parts[0]));
      }

      history.push({
        date: jobDate,
        type: 'baixa',
        description: `Uso (Ordem: ${job.orderCode || job.code || 'N/A'})`,
        qtyStringings: qtyStr,
        qtyAmount: qtyAmt
      });
    });

    // 3. Reconcile discrepancy between job-based usage and totalUsed from customer profile
    const missingStr = totalUsedStr - jobsUsedStr;
    const missingAmt = totalUsedAmt - jobsUsedAmt;

    if (missingStr !== 0 || missingAmt !== 0) {
      // Find the first entrada date to place the adjustment slightly after it
      let adjDate = new Date(2000, 0, 1);
      const entradas = history.filter(h => h.type === 'entrada').sort((a, b) => a.date.getTime() - b.date.getTime());
      if (entradas.length > 0) {
        adjDate = new Date(entradas[0].date.getTime() + 1000); // 1 second after the first purchase
      }

      history.push({
        date: adjDate,
        type: missingStr > 0 || missingAmt > 0 ? 'baixa' : 'entrada',
        description: 'Ajuste de saldo (Usos legado/manual)',
        qtyStringings: missingStr > 0 ? -missingStr : Math.abs(missingStr),
        qtyAmount: missingAmt > 0 ? -missingAmt : Math.abs(missingAmt)
      });
    }

    // Sort temporally (oldest to newest)
    history.sort((a, b) => a.date.getTime() - b.date.getTime());

    // 4. Calculate running balances
    let currentStr = 0;
    let currentAmt = 0;
    const mappedHistory = history.map(item => {
      currentStr += item.qtyStringings;
      currentAmt += item.qtyAmount;
      return { ...item, balanceStringings: currentStr, balanceAmount: currentAmt };
    });

    // Reverse to show newest first
    return mappedHistory.reverse();
  }, [activeCustomer, prepaidJobs]);

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)', zIndex: 1100,
      display: 'flex', justifyContent: 'center', alignItems: 'center'
    }}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        style={{
          width: '100%', maxWidth: '800px', background: 'white',
          borderRadius: '12px', overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
        }}>
        
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '24px', borderBottom: '1px solid #E5E7EB' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#111827' }}>Extrato do Pré-pago</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280' }}>
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
          <div style={{ overflowX: 'auto', border: '1px solid #E5E7EB', borderRadius: '8px', marginBottom: '16px', maxHeight: '400px', overflowY: 'auto' }}>
            <table style={{ width: '100%', textAlign: 'left', borderCollapse: 'collapse' }}>
              <thead style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB', position: 'sticky', top: 0, zIndex: 10 }}>
                <tr>
                  <th style={{ padding: '16px', fontSize: '13px', color: '#374151', fontWeight: 600 }}>Data</th>
                  <th style={{ padding: '16px', fontSize: '13px', color: '#374151', fontWeight: 600 }}>Descrição</th>
                  <th style={{ padding: '16px', fontSize: '13px', color: '#374151', fontWeight: 600 }}>Movimentação</th>
                  <th style={{ padding: '16px', fontSize: '13px', color: '#374151', fontWeight: 600 }}>Saldo (Encord.)</th>
                  <th style={{ padding: '16px', fontSize: '13px', color: '#374151', fontWeight: 600 }}>Saldo (R$)</th>
                </tr>
              </thead>
              <tbody>
                {history.length > 0 ? history.map((h: any, idx: number) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #E5E7EB', background: h.type === 'entrada' ? 'rgba(16, 185, 129, 0.05)' : 'white' }}>
                    <td style={{ padding: '16px', fontSize: '14px', color: '#111827' }}>{h.date.toLocaleDateString('pt-BR')}</td>
                    <td style={{ padding: '16px', fontSize: '14px', color: '#111827', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {h.type === 'entrada' ? <ArrowDownRight size={18} color="#10B981" /> : <ArrowUpRight size={18} color="#EF4444" />}
                      {h.description}
                    </td>
                    <td style={{ padding: '16px', fontSize: '14px', color: h.type === 'entrada' ? '#10B981' : '#EF4444', fontWeight: 600 }}>
                      {h.qtyStringings !== 0 ? `${h.qtyStringings > 0 ? '+' : ''}${h.qtyStringings} encord.` : `${h.qtyAmount > 0 ? '+' : ''}R$ ${Math.abs(h.qtyAmount).toFixed(2)}`}
                    </td>
                    <td style={{ padding: '16px', fontSize: '14px', color: '#111827', fontWeight: 600 }}>{h.balanceStringings}</td>
                    <td style={{ padding: '16px', fontSize: '14px', color: '#111827' }}>{h.balanceAmount.toFixed(2)}</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={5} style={{ padding: '24px', textAlign: 'center', color: '#6B7280', fontSize: '14px', background: '#F9FAFB' }}>
                      Nenhum histórico disponível
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div style={{ fontSize: '14px', color: '#6B7280', marginTop: '16px', textAlign: 'center' }}>
            Mostrando o histórico completo de entradas e usos.
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '24px', borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'flex-end', background: '#F9FAFB' }}>
            <button onClick={onClose} style={{ background: '#E5E7EB', color: '#374151', border: 'none', padding: '10px 24px', borderRadius: '8px', fontWeight: 600, fontSize: '15px', cursor: 'pointer' }}>
              Fechar
            </button>
        </div>
      </motion.div>
    </div>
  );
};
